import React, {memo, useCallback, useMemo, useState} from 'react';

import './index.scss';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {useDispatch, useSelector} from 'react-redux';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {CameraModel, CameraResolution} from '../../../../types/control';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useLocalization} from "../../../../hooks/localization";
import {useRPC} from '../../../../hooks/rpc';
import {changeBusyAction} from '../../../../store/ui/actions';

const keyExtractor = (item: CameraModel) => item.id;

export const CameraListScreen = memo(function CameraListScreen() {
    const cameraList = useSelector(state => state.control.cameraList);
    const cameraBrandSet = useSetSelector(state => state.control.cameraBrandList);
    const {t} = useLocalization();
    const [rpc] = useRPC();
    const dispatch = useDispatch()
    const [selectedCamera, setSelectedCamera] = useState<CameraModel>(null);
    const [cameraResolution, setCameraResolution] = useState<CameraResolution>(null)

    const onClickReadCameraResolution = useCallback((id: string) => {
        dispatch(changeBusyAction(true))
        rpc.ReadCameraResolution(id)
            .then(camera => setCameraResolution(camera))
            .then(() => dispatch(changeBusyAction(false)))
            .catch(() => dispatch(changeBusyAction(false)))
        }, [rpc, dispatch])

    const buttons = useMemo(() => [
        {
            text: 'TEST',
            onClick: () => onClickReadCameraResolution(selectedCamera?.id),
            className: 'blue',
            hidden: !(selectedCamera),
        },
        {
            text: t('edit'),
            url: `/settings/camera/edit/${selectedCamera ? selectedCamera.id : ''}`,
            className: 'orange',
            hidden: !(selectedCamera),
        },
        {
            text: t('create_camera'),
            url: '/settings/camera/edit',
            className: 'green',
        }
    ], [t, selectedCamera, onClickReadCameraResolution]);


    const displayNameExtractor = useCallback((item: CameraModel | any, column: ColumnModel) => {
        if (column.key === 'camera_brand_id') {
            return cameraBrandSet[item.camera_brand_id].name;
        }
        if (column.key === "resolution") {
           return item?.id === cameraResolution?.camera_id ? (`${cameraResolution?.width}x${cameraResolution?.height}`) : "-"
        }
        return item[column.key] || '-';
    }, [cameraBrandSet, cameraResolution]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 3,
            name: t('camera_name'),
            key: 'name',
        },
        {
            length: 2,
            name: t('brand'),
            key: 'camera_brand_id',
        },
        {
            length: 3,
            name: t('resolution'),
            key: "resolution"
        }
    ], [t]);

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer buttons={buttons} className="camera-list-page" backUrl={'/settings'}>
                <SettingsList
                    columns={columns}
                    items={cameraList}
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                    selectedItem={selectedCamera}
                    onItemSelected={setSelectedCamera}/>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
